/**
 * Typed calls on route contracts (plan §4.9):
 *
 *   import { navContract } from '@dataroom/contracts/nav';
 *   const nav = await api.call(navContract);                         // NavDTO
 *   const members = await api.call(directoryMembersContract, { query: { status: 'all' } });
 *   await api.call(updateThing, { params: { id }, body: { name } }, { signal });
 *
 * Path parameters, query and body are typed from the contract (a wrong name or type is a
 * compile error); the result is typed from the response schema. Errors stay `ApiError` (with
 * `issues` for `validation_failed`).
 */
import {
  type AnyContract,
  type ContractInput,
  type ContractResponse,
  type InputOptional,
  type QueryValue,
  buildPath,
  serializeQuery,
} from '@dataroom/contracts';
import type { Http } from './http';

export type CallOptions = {
  signal?: AbortSignal;
  /** Extra request headers (e.g. `if-match`). */
  headers?: Record<string, string>;
};

/** `call(contract)` when nothing is required, `call(contract, input)` otherwise. */
export type CallArgs<C extends AnyContract> =
  InputOptional<C> extends true
    ? [input?: ContractInput<C>, options?: CallOptions]
    : [input: ContractInput<C>, options?: CallOptions];

export type Call = <C extends AnyContract>(contract: C, ...args: CallArgs<C>) => Promise<ContractResponse<C>>;

/** URL (relative to the client's base URL) of a contract call: path with parameters and query. */
export function contractUrl<C extends AnyContract>(contract: C, input?: ContractInput<C>): string {
  const i = (input ?? {}) as { params?: Record<string, unknown>; query?: Record<string, QueryValue> };
  return `${buildPath(contract.path, i.params)}${serializeQuery(i.query)}`;
}

export function createCall(http: Http): Call {
  return async <C extends AnyContract>(contract: C, ...args: CallArgs<C>) => {
    const [input, options] = args as [ContractInput<C> | undefined, CallOptions | undefined];
    const body = (input as { body?: unknown } | undefined)?.body;
    return http.request<ContractResponse<C>>(contract.method, contractUrl(contract, input), {
      ...(contract.body !== undefined ? { body: body ?? {} } : {}),
      ...(options?.signal ? { signal: options.signal } : {}),
      ...(options?.headers ? { headers: options.headers } : {}),
    });
  };
}
